import { useTranslation } from 'react-i18next'

import { LOCAL_STORAGE_BIRDVIEW_KPIS_ORDER_KEY } from '../../../constants/storage'
import { getItemsIds } from '../../../utils/common'
import { BirdviewChartCard } from '../BirdviewChartCard'
import { BirdviewChartCatalog } from '../BirdviewChartCatalog'
import {
  BirdviewChartGrid,
  useBirdviewChartGridHidden,
} from '../BirdviewChartGrid'
import { BirdviewControl } from '../BirdviewControl'
import { BirdviewHeaderActionsPortal } from '../BirdviewHeader'
import { BirdviewSparkline } from '../BirdviewSparkline'
import { BirdviewTileRow } from '../BirdviewTileRow'
import { BirdviewSection } from '../DashboardBirdview.constants'
import { checkIsBirdviewTilesLoading } from '../DashboardBirdview.utils.sections'
import { getBirdviewStorageKey } from '../DashboardBirdview.utils.storage'
import { DashboardTile } from '../DashboardTile'

import {
  BIRDVIEW_ANALYTICS_PERIOD_OPTIONS,
  BIRDVIEW_ANALYTICS_TILES_PER_ROW,
} from './BirdviewAnalytics.constants'
import {
  checkIsBirdviewAnalyticsPeriodAvailable,
  getBirdviewAnalyticsCatalogRows,
  getBirdviewAnalyticsCharts,
  getBirdviewAnalyticsDefaultHiddenIds,
  getBirdviewAnalyticsHiddenRows,
  getBirdviewAnalyticsHiddenStorageKey,
  getBirdviewAnalyticsKpis,
  getBirdviewAnalyticsPeriod,
  getBirdviewAnalyticsSoonRows,
} from './BirdviewAnalytics.utils'

function BirdviewAnalytics({
  chartControls,
  preset,
  sections,
  storageScope,
  onChangeChartControls,
  onNavigate,
  onResetLayout,
}) {
  const { t } = useTranslation()

  const kpis = getBirdviewAnalyticsKpis({
    preset,
    section: sections[BirdviewSection.Kpis],
    t,
  })
  const charts = getBirdviewAnalyticsCharts({ preset, sections, t })
  const chartIds = getItemsIds(charts)

  const { hiddenIds, hideItem, showItem } = useBirdviewChartGridHidden({
    defaultIds: getBirdviewAnalyticsDefaultHiddenIds(charts),
    ids: chartIds,
    storageKey: getBirdviewAnalyticsHiddenStorageKey({
      ids: chartIds,
      storageScope,
    }),
  })

  const catalogRows = getBirdviewAnalyticsCatalogRows({
    charts,
    hiddenIds,
    t,
  })
  const hiddenRows = getBirdviewAnalyticsHiddenRows(catalogRows)

  function handleToggleChart(id, isShown) {
    if (isShown) {
      showItem(id)
    } else {
      hideItem(id)
    }
  }

  function handleChangePeriod(period) {
    onChangeChartControls(BirdviewSection.Kpis, { period })
  }

  const catalogProps = {
    shownCount: catalogRows.length - hiddenRows.length,
    totalCount: catalogRows.length,
    onReset: onResetLayout,
    onToggle: handleToggleChart,
  }

  function renderKpi(kpi) {
    return (
      <DashboardTile
        dragHandle
        Chart={BirdviewSparkline}
        chartProps={{ points: kpi.points, tone: kpi.tone, unit: kpi.unit }}
        count={kpi.count}
        countTooltip={kpi.countTooltip}
        delta={kpi.delta}
        deltaTone={kpi.deltaTone}
        info={kpi.info}
        isRefreshing={kpi.isRefreshing}
        label={kpi.label}
        state={kpi.state}
      />
    )
  }

  function renderChart({ dragHandleProps, item: chart, rowSpan, onCycleSize }) {
    return (
      <BirdviewChartCard
        chart={chart}
        controls={chartControls[chart.sectionKey]}
        dragHandleProps={dragHandleProps}
        rowSpan={rowSpan}
        section={chart.section}
        title={chart.title}
        onChangeControls={onChangeChartControls}
        onCycleSize={onCycleSize}
        onNavigate={onNavigate}
        onRemove={hideItem}
      />
    )
  }

  return (
    <>
      <BirdviewHeaderActionsPortal>
        {checkIsBirdviewAnalyticsPeriodAvailable(preset) ? (
          <BirdviewControl
            options={BIRDVIEW_ANALYTICS_PERIOD_OPTIONS}
            value={getBirdviewAnalyticsPeriod(chartControls)}
            onChange={handleChangePeriod}
          />
        ) : null}
        <BirdviewChartCatalog
          {...catalogProps}
          rows={[
            ...catalogRows,
            ...getBirdviewAnalyticsSoonRows({ preset, t }),
          ]}
        />
      </BirdviewHeaderActionsPortal>
      <BirdviewTileRow
        columns={BIRDVIEW_ANALYTICS_TILES_PER_ROW}
        isLocked={checkIsBirdviewTilesLoading(kpis)}
        items={kpis}
        renderItem={renderKpi}
        storageKeyPrefix={getBirdviewStorageKey(
          LOCAL_STORAGE_BIRDVIEW_KPIS_ORDER_KEY,
          [storageScope]
        )}
      />
      <BirdviewChartGrid
        catalogProps={{ ...catalogProps, rows: hiddenRows }}
        hiddenIds={hiddenIds}
        items={charts}
        renderItem={renderChart}
        storageScope={storageScope}
      />
    </>
  )
}

export default BirdviewAnalytics
